import { useState } from "react";
import { IconCopy, IconPlayerPlayFilled } from "@tabler/icons-react";
import { Button } from "@/lib/components/Button/Button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/lib/components/Tooltip";

export function InspirationPromptListItem({
  title,
  prompt,
  thumbnailSrc,
  previewVideoSrc,
  ctaVariant,
  onUse,
  onCopy,
}: {
  title: string;
  prompt: string;
  thumbnailSrc: string;
  previewVideoSrc?: string;
  ctaVariant: "default" | "tertiary";
  onUse: () => void;
  onCopy: () => void;
}) {
  const [isActive, setIsActive] = useState(false);

  const handleActivate = () => {
    if (!previewVideoSrc) return;
    setIsActive(true);
  };

  const handleDeactivate = () => {
    if (!previewVideoSrc) return;
    setIsActive(false);
  };

  return (
    <div className="flex flex-row gap-5">
      <div
        className="relative size-32 shrink-0 overflow-hidden rounded-[8px] border border-[#333333] bg-text-primary"
        onMouseEnter={handleActivate}
        onMouseLeave={handleDeactivate}
      >
        <img
          className="absolute inset-0 size-full object-cover"
          src={thumbnailSrc}
          alt=""
        />
        {previewVideoSrc && isActive && (
          <video
            className="absolute inset-0 size-full object-cover"
            src={previewVideoSrc}
            autoPlay
            muted
            loop
            playsInline
          />
        )}
        {previewVideoSrc && !isActive && (
          <div className="absolute bottom-0 left-0 flex size-10 items-end justify-start bg-radial-[at_bottom_left] from-black/30 to-transparent to-70% p-2.5">
            <IconPlayerPlayFilled className="text-white/90" size={12} />
          </div>
        )}
      </div>

      <div className="flex flex-1 flex-col justify-center">
        <span className="text-base leading-5 font-semibold text-white">
          {title}
        </span>
        <Tooltip>
          <TooltipTrigger asChild>
            <p className="mt-0.5 line-clamp-2 cursor-default text-sm text-white/75">
              {prompt}
            </p>
          </TooltipTrigger>
          <TooltipContent
            side="bottom"
            align="start"
            variant="secondary"
            className="max-w-xs whitespace-pre-line"
          >
            {prompt}
          </TooltipContent>
        </Tooltip>
        <div className="mt-3 flex items-center gap-3">
          <Button size="xs" variant={ctaVariant} onClick={onUse}>
            Use prompt
          </Button>
          <Button
            size="iconXs"
            variant="outline"
            onClick={onCopy}
            aria-label="Copy prompt"
          >
            <IconCopy size={16} />
          </Button>
        </div>
      </div>
    </div>
  );
}
